import { useCallback } from 'react'
import { BotAuthorizationDisplay } from '@components/BotAuthorizationDisplay/BotAuthorizationDisplay'
import { ModalContainer } from '@components/Modals/ModalContainer'
import { popupToast } from '@components/Notifications/popupToast'
import { StandardToast } from '@components/Notifications/StandardToast'
import { createPrivyNotAuthenticatedNotification } from '@components/Notifications/utils'
import { Box, Button, IconButton, Stack, Text } from '@ui'
import { type GetSigner, ReauthenticateButton, WalletReady } from 'privy/WalletReady'
import { type BotData, useUpdateAppTransaction } from 'use-towns-client'
import { type Address } from 'viem'

interface BotAuthorizeUpdateDialogProps {
    isOpen: boolean
    onClose: () => void
    bot: BotData | undefined
    appAddress: Address
    spaceAddress: Address
}

export const BotAuthorizeUpdateDialog = ({
    isOpen,
    onClose,
    bot,
    appAddress,
    spaceAddress,
}: BotAuthorizeUpdateDialogProps) => {
    const { execute: updateApp, isLoading: isUpdatingApp } = useUpdateAppTransaction({
        onSuccess: () => {
            popupToast(({ toast }) => (
                <StandardToast.Success toast={toast} message="Bot upgraded successfully" />
            ))
            onClose()
        },
        onError: (error) => {
            console.error('Bot update failed:', error)
            popupToast(({ toast }) => (
                <StandardToast.Error
                    toast={toast}
                    message="Failed to update bot"
                    subMessage="Please try again or contact support if the problem persists."
                />
            ))
        },
    })

    const handleUpdate = useCallback(
        async (getSigner: GetSigner) => {
            if (!bot?.clientAddress || !bot.latestAppId) {
                return
            }
            const signer = await getSigner()
            if (!signer) {
                createPrivyNotAuthenticatedNotification()
                return
            }
            updateApp({
                clientAddress: bot.clientAddress,
                appAddress,
                spaceAddress,
                signer,
                latestAppId: bot.latestAppId,
            })
        },
        [bot?.clientAddress, bot?.latestAppId, updateApp, appAddress, spaceAddress],
    )

    if (!isOpen) {
        return null
    }

    return (
        <ModalContainer padding="none" disableBackgroundClick={isUpdatingApp} onHide={onClose}>
            <Stack gap="sm">
                <Stack
                    horizontal
                    alignItems="center"
                    justifyContent="center"
                    padding="lg"
                    position="relative"
                >
                    <Text fontWeight="strong" color="default">
                        Update Bot
                    </Text>
                    <IconButton
                        position="absolute"
                        style={{ right: '16px' }}
                        icon="close"
                        size="square_sm"
                        disabled={isUpdatingApp}
                        onClick={onClose}
                    />
                </Stack>
            </Stack>

            <Box padding="lg" minHeight="400" maxHeight="500" overflow="auto">
                <BotAuthorizationDisplay
                    bot={bot}
                    label="the latest version of the bot requests the following permissions"
                />
            </Box>

            <Box padding="lg" paddingTop="md" borderTop="default">
                <Stack horizontal justifyContent="end" gap="sm">
                    <Button
                        tone="level2"
                        size="button_md"
                        disabled={isUpdatingApp}
                        onClick={onClose}
                    >
                        Cancel
                    </Button>
                    <WalletReady
                        WaitForWallets={<Button disabled>Checking wallet...</Button>}
                        WaitForPrivy={<Button disabled>Authenticating...</Button>}
                        LoginButton={<ReauthenticateButton />}
                    >
                        {({ getSigner }) => (
                            <Button
                                tone={isUpdatingApp ? 'level2' : 'cta1'}
                                disabled={isUpdatingApp}
                                onClick={() => handleUpdate(getSigner)}
                            >
                                {isUpdatingApp ? 'Updating...' : 'Update Bot'}
                            </Button>
                        )}
                    </WalletReady>
                </Stack>
            </Box>
        </ModalContainer>
    )
}
